import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, X, UserPlus, Receipt } from "lucide-react";
import { SERVICE_TYPES, URGENCY_LEVELS } from "@/lib/constants";
import QuotePicker from "@/components/QuotePicker";

const blank = {
  full_name: "",
  email: "",
  phone: "",
  scooter_brand: "",
  scooter_model: "",
  issue_description: "",
  issue_started: "",
  is_rideable: true,
  service_type: "general",
  preferred_dropoff_at: "",
  urgency: "medium",
  media: [],
  pickup_required: false,
  dropoff_required: false,
  service_address: "",
};

const NewJobModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(blank);
  const [quoteItems, setQuoteItems] = useState([]);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setVal = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    // Lightweight validation
    if (!form.full_name.trim()) return toast.error("Customer name required");
    if (!form.email.trim()) return toast.error("Email required");
    if (!form.phone.trim()) return toast.error("Phone required");
    if (!form.scooter_brand.trim() || !form.scooter_model.trim()) return toast.error("Scooter brand + model required");
    if (!form.issue_description.trim()) return toast.error("Brief issue description required");
    if (!form.preferred_dropoff_at) return toast.error("Drop-off date/time required");
    if ((form.pickup_required || form.dropoff_required) && !form.service_address.trim()) {
      return toast.error("Address required for pickup/drop-off");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        scooter_brand: form.scooter_brand.trim(),
        scooter_model: form.scooter_model.trim(),
        issue_description: form.issue_description.trim(),
        issue_started: form.issue_started.trim() || "Not specified",
        preferred_dropoff_at: new Date(form.preferred_dropoff_at).toISOString(),
        quote_items: quoteItems,
        service_address: (form.pickup_required || form.dropoff_required) ? form.service_address.trim() : null,
      };
      const res = await api.post("/admin/jobs", payload, { params: { notify_customer: notify } });
      toast.success(`Job ${res.data.ref_number} created`);
      setForm(blank);
      setQuoteItems([]);
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  const estTotal = quoteItems.reduce((s, v) => s + Number(v.price || 0) * Number(v.qty || 1), 0)
    + (form.pickup_required ? 40 : 0)
    + (form.dropoff_required ? 40 : 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="new-job-modal" role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl rounded-xl border border-spark-teal/30 bg-spark-ink shadow-2xl overflow-hidden">
        <div className="bg-spark-teal/10 border-b border-spark-teal/30 px-6 py-4 flex items-center gap-3">
          <UserPlus className="size-5 text-spark-teal" />
          <h2 className="font-display uppercase tracking-wider text-base font-bold text-spark-teal flex-1">New booking (manual)</h2>
          {quoteItems.length > 0 || form.pickup_required || form.dropoff_required ? (
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-spark-yellow bg-spark-yellow/10 px-2 py-1 rounded-sm" data-testid="nj-quote-badge">
              <Receipt className="size-3" /> Est ${estTotal.toFixed(2)}
            </span>
          ) : null}
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-white p-1 rounded-sm hover:bg-white/5" aria-label="Close" data-testid="new-job-close">
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 grid sm:grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto">
          <Field label="Customer name" required>
            <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.full_name} onChange={set("full_name")} placeholder="e.g. Mere Johnson" data-testid="nj-name" />
          </Field>
          <Field label="Phone" required>
            <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.phone} onChange={set("phone")} placeholder="022 555 0000" data-testid="nj-phone" />
          </Field>
          <Field label="Email" required className="sm:col-span-2">
            <input type="email" className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.email} onChange={set("email")} placeholder="customer@example.com" data-testid="nj-email" />
          </Field>
          <Field label="Scooter brand" required>
            <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.scooter_brand} onChange={set("scooter_brand")} placeholder="Xiaomi" data-testid="nj-brand" />
          </Field>
          <Field label="Scooter model" required>
            <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.scooter_model} onChange={set("scooter_model")} placeholder="Pro 2" data-testid="nj-model" />
          </Field>
          <Field label="Service type" required>
            <select className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.service_type} onChange={set("service_type")} data-testid="nj-service">
              {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value} className="bg-spark-ink">{s.label}</option>)}
            </select>
          </Field>
          <Field label="Urgency">
            <select className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.urgency} onChange={set("urgency")} data-testid="nj-urgency">
              {URGENCY_LEVELS.map((u) => <option key={u.value} value={u.value} className="bg-spark-ink">{u.label}</option>)}
            </select>
          </Field>
          <Field label="Issue description" required className="sm:col-span-2">
            <textarea rows={3} className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.issue_description} onChange={set("issue_description")} placeholder="What's wrong with the scooter?" data-testid="nj-issue" />
          </Field>
          <Field label="When did the issue start?" className="sm:col-span-2">
            <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.issue_started} onChange={set("issue_started")} placeholder="e.g. Last week / 3 days ago" data-testid="nj-started" />
          </Field>
          <Field label="Drop-off date & time" required>
            <input type="datetime-local" className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.preferred_dropoff_at} onChange={set("preferred_dropoff_at")} data-testid="nj-dropoff" />
          </Field>
          <Field label="Currently rideable?">
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setVal("is_rideable", true)} className={`flex-1 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-bold ${form.is_rideable ? "bg-spark-teal text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid="nj-ride-yes">Yes</button>
              <button type="button" onClick={() => setVal("is_rideable", false)} className={`flex-1 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-bold ${!form.is_rideable ? "bg-spark-teal text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid="nj-ride-no">No</button>
            </div>
          </Field>

          {/* Pickup / Return logistics */}
          <div className="sm:col-span-2 pt-4 mt-1 border-t border-white/10" data-testid="nj-logistics-section">
            <h3 className="font-display uppercase tracking-wider text-xs font-bold text-spark-teal mb-2">Pickup / Return (optional) · $40 each way</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer text-sm ${form.pickup_required ? "bg-spark-teal/10 border-spark-teal text-white" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/[0.07]"}`} data-testid="nj-opt-pickup">
                <input type="checkbox" checked={form.pickup_required} onChange={(e) => setVal("pickup_required", e.target.checked)} className="size-4 accent-spark-yellow" data-testid="nj-check-pickup" />
                <span className="flex-1"><strong className="block">Pickup from customer</strong><span className="text-[11px] text-slate-400">We collect their scooter.</span></span>
                <span className="font-bold text-spark-yellow">+$40</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer text-sm ${form.dropoff_required ? "bg-spark-teal/10 border-spark-teal text-white" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/[0.07]"}`} data-testid="nj-opt-dropoff">
                <input type="checkbox" checked={form.dropoff_required} onChange={(e) => setVal("dropoff_required", e.target.checked)} className="size-4 accent-spark-yellow" data-testid="nj-check-dropoff" />
                <span className="flex-1"><strong className="block">Return to customer</strong><span className="text-[11px] text-slate-400">We deliver back when ready.</span></span>
                <span className="font-bold text-spark-yellow">+$40</span>
              </label>
            </div>
            {(form.pickup_required || form.dropoff_required) && (
              <div className="mt-3" data-testid="nj-address-row">
                <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Pickup / Return address <span className="text-spark-yellow">*</span></label>
                <textarea rows={2} className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal resize-none" value={form.service_address} onChange={set("service_address")} placeholder="123 Example St, Newtown, Wellington" data-testid="nj-address" />
                <p className="text-[11px] text-spark-yellow mt-1.5 font-bold">Logistics fee: ${(form.pickup_required ? 40 : 0) + (form.dropoff_required ? 40 : 0)}</p>
              </div>
            )}
          </div>

          {/* Quote / Estimate builder */}
          <div className="sm:col-span-2 pt-4 mt-2 border-t border-white/10" data-testid="nj-quote-section">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="size-4 text-spark-yellow" />
              <h3 className="font-display uppercase tracking-wider text-xs font-bold text-spark-yellow">Quick estimate (optional)</h3>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">Pick any services and/or parts to attach an upfront estimate to this booking — same as the customer flow, but with admin parts access.</p>
            <div className="bg-spark-floor rounded-lg p-3 sm:p-4">
              <QuotePicker value={quoteItems} onChange={setQuoteItems} tone="dark" />
            </div>
          </div>

          <div className="sm:col-span-2 pt-2 border-t border-white/5">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="nj-notify-row">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="size-4 mt-0.5 accent-spark-yellow" data-testid="nj-notify" />
              <span className="text-xs text-slate-300">
                <strong className="text-white">Send customer confirmation email</strong>
                <span className="block text-slate-400 text-[11px] mt-0.5">Untick if you've already covered everything on the phone — admin will still be notified.</span>
              </span>
            </label>
          </div>
        </form>
        <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50" data-testid="nj-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-400 disabled:opacity-60" data-testid="nj-submit">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {busy ? "Creating…" : "Create job"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, required, className = "" }) => (
  <div className={className}>
    <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
      {label} {required && <span className="text-spark-yellow">*</span>}
    </label>
    {children}
  </div>
);

export default NewJobModal;
